const TRACKABLE_LINK_SELECTOR = [
  'a.lp-inline-link',
  'a.lp-btn-primary',
  'a.lp-btn-secondary',
  '#menu a',
  'footer a'
].join(', ');
const DECORATED_CLICK_SELECTOR = '[data-telemetry-event]';
const DECORATED_SUBMIT_SELECTOR = 'form[data-telemetry-submit-event]';
const DECORATED_VIEW_SELECTOR = '[data-telemetry-view-event]';
const COPY_LINK_SELECTOR = 'button[data-copy-link-url]';
const PAGE_VIEW_EVENTS = {
  '/sign_up': 'lp_sign_up_page_view',
  '/sign_in': 'lp_sign_in_page_view',
  '/about': 'lp_trust_page_view',
  '/methodology': 'lp_trust_page_view',
  '/sec-data': 'lp_trust_page_view',
  '/contact': 'lp_trust_page_view',
  '/privacy': 'lp_trust_page_view',
  '/terms': 'lp_trust_page_view',
  '/advertising-disclosure': 'lp_trust_page_view'
};

function pushDataLayerEvent(eventName, payload = {}) {
  if (!Array.isArray(window.dataLayer)) return;

  window.dataLayer.push({
    event: eventName,
    ...payload
  });
}

function compactText(value) {
  return (value || '').replace(/\s+/g, ' ').trim().slice(0, 120);
}

function normalizePath(rawHref) {
  if (!rawHref) return '';

  try {
    const parsed = new URL(rawHref, window.location.origin);
    return parsed.pathname;
  } catch (_error) {
    return '';
  }
}

function csrfToken() {
  const token = document.querySelector('meta[name="csrf-token"]');
  return token ? token.content : null;
}

function inferPageType(pathname) {
  if (pathname === '/') return 'home';
  if (pathname === '/search' || pathname === '/home_search') return 'search';
  if (pathname === '/sign_up') return 'sign_up';
  if (pathname === '/sign_in') return 'sign_in';
  if (
    pathname === '/about' ||
    pathname === '/methodology' ||
    pathname === '/sec-data' ||
    pathname === '/contact' ||
    pathname === '/privacy' ||
    pathname === '/terms' ||
    pathname === '/advertising-disclosure'
  ) return 'trust';
  if (pathname.startsWith('/watchlists')) return 'watchlist';
  if (
    pathname.startsWith('/filer/') ||
    pathname.startsWith('/report/') ||
    pathname.startsWith('/insider/') ||
    pathname.startsWith('/stock/') ||
    pathname.startsWith('/portfolio/') ||
    pathname.startsWith('/investment/') ||
    pathname.startsWith('/schedule/') ||
    pathname.startsWith('/fin/')
  ) return 'detail';

  return 'content';
}

function sanitizeTelemetryPayload(payload = {}) {
  const result = {};
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (typeof value === 'string') {
      const compacted = compactText(value);
      if (!compacted) return;
      result[key] = compacted;
      return;
    }
    result[key] = value;
  });
  return result;
}

function telemetryEndpoint() {
  return document.body?.dataset?.telemetryInteractionUrl || '';
}

function pageviewEndpoint() {
  return document.body?.dataset?.analyticsPageviewUrl || '';
}

function postFirstPartyPageview() {
  const endpoint = pageviewEndpoint();
  if (!endpoint) return;

  const body = JSON.stringify({
    path: window.location.pathname,
    referrer: document.referrer,
    viewport: `${window.innerWidth}x${window.innerHeight}`
  });

  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    const sent = navigator.sendBeacon(endpoint, blob);
    if (sent) return;
  }

  if (typeof fetch !== 'function') return;

  fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body,
    credentials: 'same-origin',
    keepalive: true
  }).catch(() => {});
}

function postInteractionEvent(eventName, payload = {}) {
  const endpoint = telemetryEndpoint();
  if (!endpoint) return;

  const body = JSON.stringify({
    event: eventName,
    ...payload
  });

  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    const sent = navigator.sendBeacon(endpoint, blob);
    if (sent) return;
  }

  const headers = { 'Content-Type': 'application/json' };
  const token = csrfToken();
  if (token) headers['X-CSRF-Token'] = token;
  if (typeof fetch !== 'function') return;

  fetch(endpoint, {
    method: 'POST',
    headers: headers,
    body: body,
    credentials: 'same-origin',
    keepalive: true
  }).catch(() => {});
}

function emitGrowthEvent(eventName, payload = {}, options = {}) {
  if (!eventName) return;

  const eventPayload = sanitizeTelemetryPayload({
    page_type: inferPageType(window.location.pathname),
    page_path: window.location.pathname,
    ...payload
  });
  pushDataLayerEvent(eventName, eventPayload);

  const sendToServer = options.sendToServer !== false;
  if (sendToServer) {
    postInteractionEvent(eventName, eventPayload);
  }
}

window.lpTrackGrowthEvent = function(eventName, payload = {}) {
  emitGrowthEvent(eventName, payload);
};

function linkPlacement(anchor) {
  if (anchor.closest('ul#menu')) return 'nav_menu';
  if (anchor.closest('.lp-hero')) return 'hero';
  if (anchor.closest('#home-quick-paths')) return 'home_quick_paths';
  if (anchor.closest('footer')) return 'footer';
  if (anchor.closest('.lp-card')) return 'content_card';

  return 'content';
}

function trackKeyLinkClicks() {
  document.addEventListener('click', (event) => {
    const anchor = event.target.closest('a');
    if (!anchor || !anchor.matches(TRACKABLE_LINK_SELECTOR)) return;

    pushDataLayerEvent('lp_link_click', {
      placement: linkPlacement(anchor),
      link_text: compactText(anchor.textContent),
      link_url: anchor.getAttribute('href') || '',
      page_path: window.location.pathname
    });
  });
}

function extractTelemetryPayload(element) {
  if (!element || !element.dataset) return {};

  const payload = {
    module: element.dataset.telemetryModule || undefined,
    cta_label: element.dataset.telemetryCtaLabel || compactText(element.textContent),
    source_label: element.dataset.telemetrySourceLabel || undefined,
    entity_id: element.dataset.telemetryEntityId || undefined,
    search_source: element.dataset.telemetrySearchSource || undefined
  };

  if (element.dataset.telemetrySearchQueryLength) {
    const normalizedLength = parseInt(element.dataset.telemetrySearchQueryLength, 10);
    if (!Number.isNaN(normalizedLength) && normalizedLength >= 0) {
      payload.search_query_length = normalizedLength;
    }
  }

  return payload;
}

function trackDecoratedInteractions() {
  document.addEventListener('click', (event) => {
    const trackedElement = event.target.closest(DECORATED_CLICK_SELECTOR);
    if (trackedElement) {
      emitGrowthEvent(trackedElement.dataset.telemetryEvent, extractTelemetryPayload(trackedElement));
    }

    const anchor = event.target.closest('a[href]');
    if (!anchor) return;

    if (normalizePath(anchor.getAttribute('href')) === '/sign_up') {
      emitGrowthEvent('lp_sign_up_funnel_entry_click', {
        module: linkPlacement(anchor),
        cta_label: compactText(anchor.textContent)
      });
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest(DECORATED_SUBMIT_SELECTOR);
    if (!form) return;

    const payload = extractTelemetryPayload(form);
    const searchInput = form.querySelector('input[name="q"]');
    if (searchInput) {
      payload.search_query_length = searchInput.value.trim().length;
    }

    emitGrowthEvent(form.dataset.telemetrySubmitEvent, payload);
  });
}

function trackCopyLinkButtons() {
  document.addEventListener('click', (event) => {
    const button = event.target.closest(COPY_LINK_SELECTOR);
    if (!button) return;

    const linkUrl = button.dataset.copyLinkUrl || window.location.href;
    const statusElement = button.dataset.copyLinkStatusTarget
      ? document.getElementById(button.dataset.copyLinkStatusTarget)
      : null;
    const setStatus = (message) => {
      if (statusElement) statusElement.textContent = message;
    };

    if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
      setStatus('Permalink shown below.');
      return;
    }

    navigator.clipboard.writeText(linkUrl).then(() => {
      setStatus('Link copied.');
    }).catch(() => {
      setStatus('Copy failed. Use the stable link below.');
    });
  });
}

function trackDecoratedViews() {
  const trackedElements = Array.from(document.querySelectorAll(DECORATED_VIEW_SELECTOR));
  if (trackedElements.length === 0) return;

  const seenElements = new WeakSet();
  const seenViewKeys = new Set();

  const emitView = (element) => {
    if (!element || seenElements.has(element)) return;

    const eventName = element.dataset.telemetryViewEvent;
    if (!eventName) return;

    const payload = extractTelemetryPayload(element);
    const viewKey = [
      eventName,
      payload.module || '',
      payload.cta_label || '',
      payload.source_label || '',
      payload.entity_id || ''
    ].join('|');
    if (seenViewKeys.has(viewKey)) {
      seenElements.add(element);
      return;
    }

    seenViewKeys.add(viewKey);
    seenElements.add(element);
    emitGrowthEvent(eventName, payload);
  };

  if (!('IntersectionObserver' in window)) {
    trackedElements.forEach((element) => emitView(element));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      if (entry.intersectionRatio < 0.15) return;

      const element = entry.target;
      emitView(element);
      observer.unobserve(element);
    });
  }, {
    threshold: [0.15, 0.35],
    rootMargin: '0px 0px -10% 0px'
  });

  trackedElements.forEach((element) => observer.observe(element));
}

function trackPageViews() {
  const eventName = PAGE_VIEW_EVENTS[window.location.pathname];
  if (!eventName) return;

  emitGrowthEvent(eventName, {
    module: 'page_view'
  });
}

function trackEngagementMilestones() {
  const firedDepths = new Set();
  const depthMarks = [25, 50, 75];
  let engagedFired = false;
  let interacted = false;
  let timerPassed = false;

  const maybeFireEngaged = () => {
    if (engagedFired) return;
    if (!(timerPassed && (interacted || firedDepths.has(25)))) return;

    engagedFired = true;
    pushDataLayerEvent('lp_engaged_session_hint', {
      page_path: window.location.pathname,
      max_scroll_depth: Math.max(0, ...Array.from(firedDepths))
    });
  };

  const checkDepth = () => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const docHeight = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
    const maxScrollable = Math.max(docHeight - viewportHeight, 1);
    const depthPercent = Math.min(100, Math.round((scrollTop / maxScrollable) * 100));

    depthMarks.forEach((mark) => {
      if (depthPercent < mark || firedDepths.has(mark)) return;

      firedDepths.add(mark);
      pushDataLayerEvent('lp_scroll_depth', {
        depth_percent: mark,
        page_path: window.location.pathname
      });
      maybeFireEngaged();
    });
  };

  ['click', 'keydown', 'touchstart'].forEach((eventName) => {
    document.addEventListener(eventName, () => {
      interacted = true;
      maybeFireEngaged();
    }, { passive: true, once: true });
  });

  document.addEventListener('scroll', checkDepth, { passive: true });
  checkDepth();

  window.setTimeout(() => {
    timerPassed = true;
    maybeFireEngaged();
  }, 30000);
}

function observeAdRender(slotElement, adNode) {
  let renderedEventSent = false;
  let unfilledEventSent = false;

  const emitIfRendered = () => {
    if (renderedEventSent) return;

    const adHeight = adNode.offsetHeight || 0;
    if (adHeight < 30) return;

    renderedEventSent = true;
    slotElement.dataset.lpAdRendered = 'true';

    pushDataLayerEvent('lp_ad_slot_rendered', {
      placement: slotElement.dataset.lpAdPlacement || 'content',
      ad_slot: slotElement.dataset.lpAdSlot || '',
      placement_rule: slotElement.dataset.lpAdPosition || '',
      density: slotElement.dataset.lpAdDensity || 'low',
      page_policy: document.body?.dataset?.lpAdPolicy || slotElement.dataset.lpAdPagePolicy || '',
      rendered_height: adHeight,
      page_path: window.location.pathname
    });
  };

  const emitIfUnfilled = () => {
    if (unfilledEventSent || renderedEventSent) return;
    if (adNode.getAttribute('data-ad-status') !== 'unfilled') return;

    unfilledEventSent = true;
    slotElement.dataset.lpAdRendered = 'unfilled';

    pushDataLayerEvent('lp_ad_slot_unfilled', {
      placement: slotElement.dataset.lpAdPlacement || 'content',
      ad_slot: slotElement.dataset.lpAdSlot || '',
      placement_rule: slotElement.dataset.lpAdPosition || '',
      density: slotElement.dataset.lpAdDensity || 'low',
      page_policy: document.body?.dataset?.lpAdPolicy || slotElement.dataset.lpAdPagePolicy || '',
      page_path: window.location.pathname
    });
  };

  emitIfRendered();
  emitIfUnfilled();

  const observer = new MutationObserver(() => {
    emitIfRendered();
    emitIfUnfilled();
  });
  observer.observe(adNode, { attributes: true, attributeFilter: ['style', 'class', 'data-ad-status'] });
  window.setTimeout(() => observer.disconnect(), 15000);
}

function requestAd(slotElement) {
  if (slotElement.dataset.lpAdRequested === 'true') return;

  const adNode = slotElement.querySelector('ins.adsbygoogle');
  if (!adNode) return;

  slotElement.dataset.lpAdRequested = 'true';
  slotElement.classList.add('is-requested');

  pushDataLayerEvent('lp_ad_slot_requested', {
    placement: slotElement.dataset.lpAdPlacement || 'content',
    ad_slot: slotElement.dataset.lpAdSlot || '',
    placement_rule: slotElement.dataset.lpAdPosition || '',
    density: slotElement.dataset.lpAdDensity || 'low',
    page_policy: document.body?.dataset?.lpAdPolicy || slotElement.dataset.lpAdPagePolicy || '',
    page_path: window.location.pathname
  });

  try {
    window.adsbygoogle = window.adsbygoogle || [];
    window.adsbygoogle.push({});
    observeAdRender(slotElement, adNode);
  } catch (_error) {
    slotElement.dataset.lpAdRequested = 'error';
  }
}

function initLazyAds() {
  const slots = Array.from(document.querySelectorAll('.lp-ad-slot[data-lp-ad-slot]'));
  if (slots.length === 0) return;

  let observer = null;
  let started = false;

  const startRequests = () => {
    if (started) return;
    if (!marketingConsentGranted()) return;
    started = true;

    if (!('IntersectionObserver' in window)) {
      slots.forEach((slot) => requestAd(slot));
      return;
    }

    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const slot = entry.target;
        requestAd(slot);
        observer.unobserve(slot);
      });
    }, { rootMargin: '300px 0px' });

    slots.forEach((slot) => observer.observe(slot));
  };

  window.addEventListener('lp_marketing_consent_changed', (event) => {
    if (!event.detail?.granted) return;
    startRequests();
  });

  startRequests();
}

function marketingConsentGranted() {
  if (document.body?.dataset?.lpConsentManager === 'google') return true;

  return document.body?.dataset?.lpMarketingConsent === 'granted';
}

document.addEventListener('DOMContentLoaded', function () {
  postFirstPartyPageview();
  trackPageViews();
  trackKeyLinkClicks();
  trackCopyLinkButtons();
  trackDecoratedInteractions();
  trackDecoratedViews();
  trackEngagementMilestones();
  initLazyAds();
});
